export const isServiceWorkerSupported = () => 'serviceWorker' in navigator;

export const getServiceWorkerRegistration = async () => {
  return navigator.serviceWorker.getRegistration();
};

/**
 * Retrieves service worker registration. If there is no registration, create it.
 */
export const getOrRegisterServiceWorker = async (url: string) => {
  const activeRegistration = await getServiceWorkerRegistration();

  if (activeRegistration) {
    return activeRegistration;
  }

  const registration = await navigator.serviceWorker.register(url);

  return registration;
};

/**
 * Chrome <= 75 doesn't support base64-encoded VAPID key. For backward compatibility, VAPID key
 * submitted to pushManager#subscribe must be of type Uint8Array.
 *
 * Helper function that converts string to Uint8Array. This function is needed to convert server public key for
 * subscribing to push service.
 * @see [MDN]{@link https://developer.mozilla.org/en-US/docs/Web/API/PushManager/subscribe#parameters}
 * @see [source]{@link https://github.com/GoogleChromeLabs/web-push-codelab/blob/master/app/scripts/main.js}
 */
export const urlB64ToUint8Array = (base64String: string) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export const getPushManagerSubscription = (registration: ServiceWorkerRegistration) =>
  registration.pushManager.getSubscription();

/**
 * Retrieves push manager subscription. If there is no subscription, create it with provided public key.
 */
export const getOrCreatePushManagerSubscription = async (
  registration: ServiceWorkerRegistration,
  publicKey: string
) => {
  const activeSubscription = await getPushManagerSubscription(registration);

  if (activeSubscription) {
    return activeSubscription;
  }

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlB64ToUint8Array(publicKey),
  });

  return subscription;
};

export const clientUnsubscribe = async (clientPushSubscription: PushSubscription) => {
  return clientPushSubscription.unsubscribe();
};
